/**
 * Forgetting the previous account's local data.
 *
 * Several stores keep per-person data on this device and outlive a sign-out:
 * the search recents, the check-in dismissals, the trainer's composer drafts,
 * a half-finished intake and the active workout. The next person on the phone
 * would see them. This is the runner; `provider.tsx` holds the ONE list of
 * what belongs to an account and registers it on
 * {@link AccountScope.onLeave} — so the list runs on a deliberate sign-out and
 * when a DIFFERENT account signs in, never when a refresh fails and the same
 * person signs back in.
 *
 * ── EVERY STEP RUNS, WHATEVER THE OTHERS DO ────────────────────────────────
 * `Promise.allSettled`, not a loop that awaits: a store whose storage throws
 * (a private window, a full quota, a plugin that is not there) must not leave
 * the drafts of the person who just signed out on the screen of the person who
 * just signed in. Each failure is reported with the name of its step, because
 * "account cleanup failed" without one says nothing about what survived.
 *
 * ── NATIVE-FREE ON PURPOSE ─────────────────────────────────────────────────
 * The steps are passed in, so this file imports no store, no Capacitor and no
 * `window`, and its test runs in Node (the T11 split, the same reason
 * `account.ts` takes its storage).
 *
 * It never touches the outbox. That account's queued writes stay stamped with
 * its id (T28/T34), hidden while someone else is signed in, and sent when it
 * comes back — they are its data, not this device's.
 */

/** One step and what it threw. */
export interface AccountCleanupFailure {
  readonly error: unknown;
  readonly step: string;
}

/** What to forget, by name. A step may be synchronous. */
export type AccountCleanupSteps = Readonly<
  Record<string, () => Promise<void> | void>
>;

const warn = (failure: AccountCleanupFailure): void => {
  console.warn(
    `[data] account cleanup step failed: ${failure.step}`,
    failure.error
  );
};

/**
 * Runs every step, whatever the others do, and reports each failure by name.
 * Resolves once they have all settled; it never rejects, so a caller in
 * `onLeave` cannot be stopped by a store it does not know about.
 */
export async function runAccountCleanup(
  steps: AccountCleanupSteps,
  report: (failure: AccountCleanupFailure) => void = warn
): Promise<void> {
  const entries = Object.entries(steps);
  const settled = await Promise.allSettled(
    // Wrapped, so a step that throws SYNCHRONOUSLY is a rejection like any
    // other rather than the throw that would skip the steps after it.
    entries.map(async ([, step]) => await step())
  );
  for (const [index, [name]] of entries.entries()) {
    const result = settled[index];
    if (result?.status === "rejected") {
      report({ error: result.reason, step: name });
    }
  }
}
